import React from 'react';
import { graphql, Link } from 'gatsby';
import localize from '../components/localize';
import {
  BlogContainer,
  BlogGrid,
  BlogHero,
  BlogPostCard,
  BlogPostImage,
  BlogPostMeta,
  BlogPostText,
} from '../styles/BlogStyles';
import SEO from "../components/SEO";

function getBlogPath(locale, slug) {
  const prefix = locale === 'en' ? '/en' : '';
  return `${prefix}/blog/${slug}`;
}

function formatDate(date, locale) {
  if (!date) return null;

  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'pl-PL', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));
}

function getLocalizedValue(value, locale) {
  if (!value) return '';

  if (locale === 'en') {
    return value.en || value.pl || '';
  }

  return value.pl || value.en || '';
}

function getPostImage(post) {
  return (
    post.mainImage &&
    post.mainImage.asset &&
    post.mainImage.asset.fixed
  );
}

function BlogPage({ data, pathContext }) {
  const locale = pathContext && pathContext.locale ? pathContext.locale : 'pl';

  const blogPage = data.sanityBlogPage;
  const allPosts = data.allSanityPost.nodes;

  const featuredPosts =
    blogPage && blogPage.featuredPosts && blogPage.featuredPosts.length
      ? blogPage.featuredPosts
      : [];

  const posts = featuredPosts.length ? featuredPosts : allPosts;

  const heroTitle =
    getLocalizedValue(blogPage && blogPage.title, locale) || 'Blog';

  const heroDescription =
    getLocalizedValue(blogPage && blogPage.description, locale) ||
    (locale === 'en'
      ? 'Articles, guides and updates from Bizami.'
      : 'Artykuły, poradniki i aktualności Bizami.');

  const seoTitle = locale === 'en' ? 'Bizami Blog | Insights on Inventory and Purchasing Management' : 'Blog Bizami | Wiedza o zarządzaniu zapasami i zakupami';
  const seoDescription = locale === 'en' ? 'Practical articles on purchasing planning, inventory management, demand forecasting, and process optimization for businesses. Explore insights and tips from Bizami.' : 'Praktyczne artykuły o planowaniu zakupów, zarządzaniu zapasami, prognozowaniu popytu i optymalizacji procesów w firmie. Poznaj wiedzę i wskazówki od Bizami.';

  return (
    <>
      <SEO title={seoTitle} description={seoDescription}/>
      <BlogContainer>
        <BlogHero>
          <p>{locale === 'en' ? 'Knowledge base' : 'Baza wiedzy'}</p>
          <h1>{heroTitle}</h1>
          <span>{heroDescription}</span>
        </BlogHero>

        {posts.length ? (
          <BlogGrid>
            {posts.map(post => {
              const slug = post.slug && post.slug.current;
              const title = getLocalizedValue(post.title, locale);
              const excerpt = getLocalizedValue(post.excerpt, locale);
              const image = getPostImage(post);

              if (!slug) return null;

              return (
                <BlogPostCard key={post.id}>
                  {image && (
                    <Link to={getBlogPath(locale, slug)}>
                      <BlogPostImage
                        src={image.src}
                        srcSet={image.srcSet}
                        alt={title || 'Blog post'}
                      />
                    </Link>
                  )}

                  <BlogPostText>
                    {post.publishedAt && (
                      <BlogPostMeta>
                        {formatDate(post.publishedAt, locale)}
                      </BlogPostMeta>
                    )}

                    <h2>
                      <Link to={getBlogPath(locale, slug)}>
                        {title || 'Blog post'}
                      </Link>
                    </h2>

                    {excerpt && <p>{excerpt}</p>}

                    <Link className="read-more" to={getBlogPath(locale, slug)}>
                      {locale === 'en' ? 'Read more' : 'Czytaj więcej'}
                    </Link>
                  </BlogPostText>
                </BlogPostCard>
              );
            })}
          </BlogGrid>
        ) : (
          <p>{locale === 'en' ? 'No blog posts yet.' : 'Brak wpisów na blogu.'}</p>
        )}
      </BlogContainer>
    </>
  );
}

export default localize(BlogPage);

export const query = graphql`
    query BlogPostsPageQuery {
        sanityBlogPage {
            title {
                pl
                en
            }
            description {
                pl
                en
            }
            featuredPosts {
                id
                title {
                    pl
                    en
                }
                excerpt {
                    pl
                    en
                }
                publishedAt
                slug {
                    current
                }
                mainImage {
                    asset {
                        fixed {
                            src
                            srcSet
                        }
                    }
                }
            }
        }

        allSanityPost(sort: { fields: publishedAt, order: DESC }) {
            nodes {
                id
                title {
                    pl
                    en
                }
                excerpt {
                    pl
                    en
                }
                publishedAt
                slug {
                    current
                }
                mainImage {
                    asset {
                        fixed {
                            src
                            srcSet
                        }
                    }
                }
            }
        }
    }
`;